import { generateWhatsAppMessageWithPublicLink } from '../utils/invoiceEncoding';

class InvoiceImageService {
  constructor() {
    this.canvas = null;
    this.ctx = null;
  }

  // Generate invoice as image
  async generateInvoiceImage(invoiceData, orgData) {
    return new Promise(async (resolve, reject) => {
      try {
        // Create canvas with higher resolution for better quality
        const scale = 2; // 2x resolution for crisp images
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        
        // Set canvas size with scaling
        canvas.width = 800 * scale;
        canvas.height = 1000 * scale;
        
        // Enable high-quality rendering
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        
        // Scale the context for high DPI
        ctx.scale(scale, scale);
        
        // Set background
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, 800, 1000);
        
        // Load logo first if available
        let logoImage = null;
        if (orgData?.logoDataUrl) {
          try {
            logoImage = await this.loadImage(orgData.logoDataUrl);
          } catch (error) {
            console.warn('Failed to load logo:', error);
          }
        }
        
        // Generate invoice content using unified logic with logo
        this.drawUnifiedInvoice(ctx, invoiceData, orgData, 800, logoImage);
        
        // Convert to blob with high quality
        canvas.toBlob((blob) => {
          if (blob) {
            resolve(blob);
          } else {
            reject(new Error('Failed to generate image'));
          }
        }, 'image/png', 1.0); // Maximum quality
        
      } catch (error) {
        reject(error);
      }
    });
  }

  // Generate invoice as PDF
  async generateInvoicePDF(invoiceData, orgData) {
    return new Promise(async (resolve, reject) => {
      try {
        // Load jsPDF dynamically
        await this.loadScript('https://cdn.jsdelivr.net/npm/jspdf@2.5.1/dist/jspdf.umd.min.js');
        
        // Wait for jsPDF to be available
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        // Check if jsPDF is available
        if (typeof window.jspdf === 'undefined') {
          throw new Error('jsPDF not loaded');
        }
        
        // Create PDF using the global jsPDF
        const { jsPDF } = window.jspdf;
        const pdf = new jsPDF('p', 'mm', 'a4');
        const pageWidth = pdf.internal.pageSize.getWidth();
        const pageHeight = pdf.internal.pageSize.getHeight();
        const margin = 20;
        
        // Normalize invoice data for consistent structure
        const normalizedData = this.normalizeInvoiceData(invoiceData);
        
        // Load logo if available
        let logoImage = null;
        if (orgData?.logoDataUrl) {
          try {
            logoImage = await this.loadImage(orgData.logoDataUrl);
          } catch (error) {
            console.warn('Failed to load logo for PDF:', error);
          }
        }
        
        let y = margin;
        const lineHeight = 6;
        const fontSize = 11;
        const headerFontSize = 16;
        const titleFontSize = 14;
        const sectionFontSize = 12;
        const itemFontSize = 10;
        
        // ===== SECTION 1: BUSINESS HEADER =====
        // Business name
        pdf.setFontSize(headerFontSize);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(16, 185, 129); // Green color
        pdf.text(orgData?.name || 'Your Business', margin, y);
        
        // Logo (if available) - right side
        if (logoImage) {
          try {
            // For PDF, we can't easily embed images without additional libraries
            // So we'll use a placeholder for now, but we have the logo loaded
            pdf.setFillColor(16, 185, 129);
            pdf.circle(pageWidth - margin - 10, y - 6, 6, 'F');
            pdf.setTextColor(255, 255, 255);
            pdf.setFontSize(6);
            pdf.text('LOGO', pageWidth - margin - 12, y - 3);
          } catch (error) {
            // Fallback to placeholder
            pdf.setFillColor(16, 185, 129);
            pdf.circle(pageWidth - margin - 10, y - 6, 6, 'F');
            pdf.setTextColor(255, 255, 255);
            pdf.setFontSize(6);
            pdf.text('LOGO', pageWidth - margin - 12, y - 3);
          }
        } else {
          // Logo placeholder (right side)
          pdf.setFillColor(16, 185, 129);
          pdf.circle(pageWidth - margin - 10, y - 6, 6, 'F');
          pdf.setTextColor(255, 255, 255);
          pdf.setFontSize(6);
          pdf.text('LOGO', pageWidth - margin - 12, y - 3);
        }
        
        y += lineHeight + 3;
        
        // Business details
        pdf.setFontSize(fontSize);
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(55, 65, 81); // Gray color
        if (orgData?.address) {
          pdf.text(orgData.address, margin, y);
          y += lineHeight;
        }
        if (orgData?.phone) {
          pdf.text(`Phone: ${orgData.phone}`, margin, y);
          y += lineHeight;
        }
        if (orgData?.email) {
          pdf.text(`Email: ${orgData.email}`, margin, y);
          y += lineHeight;
        }
        
        y += 10;
        
        // Invoice title with green background
        pdf.setFillColor(16, 185, 129);
        pdf.rect(margin, y - 3, pageWidth - (margin * 2), 8, 'F');
        pdf.setTextColor(255, 255, 255);
        pdf.setFontSize(titleFontSize);
        pdf.setFont('helvetica', 'bold');
        pdf.text(`INVOICE: ${normalizedData.billName}`, margin + 3, y + 2);
        
        // Invoice date (right side)
        pdf.text(`Date: ${new Date().toLocaleDateString()}`, pageWidth - margin - 40, y + 2);
        
        y += 12;
        
        // ===== SECTION 2: CUSTOMER DETAILS =====
        // Customer header
        pdf.setTextColor(16, 185, 129);
        pdf.setFontSize(sectionFontSize);
        pdf.setFont('helvetica', 'bold');
        pdf.text('Bill To:', margin, y);
        y += lineHeight + 2;
        
        // Customer details
        pdf.setFontSize(fontSize);
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(31, 41, 55); // Dark gray
        pdf.text(normalizedData.customerName, margin, y);
        y += lineHeight;
        pdf.text(normalizedData.customerPhone, margin, y);
        y += lineHeight + 8;
        
        // ===== SECTION 3: ITEMS TABLE =====
        // Table header with green background
        pdf.setFillColor(16, 185, 129);
        pdf.rect(margin, y - 2, pageWidth - (margin * 2), 6, 'F');
        pdf.setTextColor(255, 255, 255);
        pdf.setFontSize(itemFontSize);
        pdf.setFont('helvetica', 'bold');
        
        // Table headers with proper alignment
        pdf.text('Item Description', margin + 2, y + 1);
        pdf.text('Qty', margin + 100, y + 1);
        pdf.text('Unit Price', margin + 130, y + 1);
        pdf.text('Total', margin + 180, y + 1);
        
        y += 8;
        
        // Items list
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(31, 41, 55);
        
        normalizedData.items.forEach((item, index) => {
          const hasItemDiscount = item.discount && item.discount > 0;
          const itemHeight = hasItemDiscount ? 8 : 5;
          
          // Alternate row colors
          if (index % 2 === 0) {
            pdf.setFillColor(249, 250, 251); // Light gray
            pdf.rect(margin, y - 1, pageWidth - (margin * 2), itemHeight, 'F');
          }
          
          pdf.text(item.name, margin + 2, y + 1);
          pdf.text(item.quantity.toString(), margin + 100, y + 1);
          pdf.text(`₹${item.price.toFixed(2)}`, margin + 130, y + 1);
          
          if (hasItemDiscount) {
            const originalTotal = item.originalTotal || (item.price * item.quantity);
            // Original price (strikethrough effect with gray)
            pdf.setTextColor(156, 163, 175); // Gray
            pdf.text(`₹${originalTotal.toFixed(2)}`, margin + 180, y + 1);
            y += 2.5;
            
            // Discount in green
            pdf.setTextColor(16, 185, 129); // Green
            pdf.setFont('helvetica', 'bold');
            pdf.text(`-₹${item.discount.toFixed(2)}`, margin + 180, y + 1);
            y += 2.5;
            
            // Final total
            pdf.setTextColor(31, 41, 55); // Dark gray
            pdf.setFont('helvetica', 'bold');
            pdf.text(`₹${item.lineTotal.toFixed(2)}`, margin + 180, y + 1);
          } else {
            pdf.text(`₹${(item.price * item.quantity).toFixed(2)}`, margin + 180, y + 1);
          }
          
          pdf.setTextColor(31, 41, 55); // Reset to dark gray
          pdf.setFont('helvetica', 'normal');
          y += itemHeight;
        });
        
        y += 8;
        
        // ===== TOTAL SECTION =====
        const hasDiscount = normalizedData.discount > 0;
        const itemDiscountTotal = normalizedData.items.reduce((sum, item) => sum + (item.discount || 0), 0);
        const overallDiscount = normalizedData.discount - itemDiscountTotal;
        const totalHeight = hasDiscount ? 24 : 6;
        
        // Total background with green accent
        pdf.setFillColor(240, 253, 244);
        pdf.rect(margin, y - 2, pageWidth - (margin * 2), totalHeight, 'F');
        pdf.setFillColor(16, 185, 129);
        pdf.rect(margin, y - 2, 2, totalHeight, 'F');
        
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(55, 65, 81);
        pdf.setFontSize(fontSize);
        
        // Subtotal
        pdf.text('Subtotal:', margin + 3, y + 1);
        pdf.text(`₹${normalizedData.subtotal.toFixed(2)}`, pageWidth - margin - 10, y + 1);
        y += lineHeight;
        
        // Item-level discounts summary (if any)
        if (itemDiscountTotal > 0) {
          pdf.setFont('helvetica', 'bold');
          pdf.setTextColor(16, 185, 129);
          pdf.text('Item Discounts:', margin + 3, y + 1);
          pdf.text(`-₹${itemDiscountTotal.toFixed(2)}`, pageWidth - margin - 10, y + 1);
          y += lineHeight;
        }
        
        // Overall discount (if any)
        if (overallDiscount > 0) {
          pdf.setFont('helvetica', 'bold');
          pdf.setTextColor(16, 185, 129);
          pdf.setFontSize(sectionFontSize);
          pdf.text('Overall Discount:', margin + 3, y + 1);
          pdf.text(`-₹${overallDiscount.toFixed(2)}`, pageWidth - margin - 10, y + 1);
          y += lineHeight;
        }
        
        // Show total savings if discount is applied
        if (hasDiscount) {
          pdf.setFont('helvetica', 'bold');
          pdf.setTextColor(5, 150, 105);
          pdf.setFontSize(sectionFontSize);
          pdf.text(`🎉 You Saved ₹${normalizedData.discount.toFixed(2)}! 🎉`, pageWidth / 2, y + 1, { align: 'center' });
          y += lineHeight + 2;
        }
        
        // Total amount with proper alignment
        pdf.setTextColor(16, 185, 129);
        pdf.setFontSize(sectionFontSize);
        pdf.setFont('helvetica', 'bold');
        pdf.text('Total Amount:', margin + 3, y + 1);
        
        pdf.setFontSize(14);
        pdf.text(`₹${normalizedData.total.toFixed(2)}`, pageWidth - margin - 10, y + 1);
        
        y += 12;
        
        // ===== FOOTER SECTION =====
        // Thank you message - centered (use custom footer text if available)
        const footerText = orgData?.footerText || 'Thank you for your business!';
        pdf.setTextColor(16, 185, 129);
        pdf.setFontSize(fontSize);
        pdf.setFont('helvetica', 'bold');
        pdf.text(footerText, pageWidth / 2, y, { align: 'center' });
        y += lineHeight + 2;
        
        // Footer details - centered
        pdf.setTextColor(107, 114, 128); // Gray
        pdf.setFontSize(fontSize - 1);
        pdf.setFont('helvetica', 'normal');
        pdf.text(`Invoice generated on: ${new Date().toLocaleDateString()} at ${new Date().toLocaleTimeString()}`, pageWidth / 2, y, { align: 'center' });
        y += lineHeight;
        pdf.text('This is a computer-generated invoice.', pageWidth / 2, y, { align: 'center' });
        
        // Convert to blob
        const pdfBlob = pdf.output('blob');
        resolve(pdfBlob);
        
      } catch (error) {
        // Fallback to image generation
        try {
          const imageBlob = await this.generateInvoiceImage(invoiceData, orgData);
          resolve(imageBlob);
        } catch (fallbackError) {
          reject(fallbackError);
        }
      }
    });
  }

  // Load script dynamically
  loadScript(src) {
    return new Promise((resolve, reject) => {
      if (document.querySelector(`script[src="${src}"]`)) {
        resolve();
        return;
      }
      
      const script = document.createElement('script');
      script.src = src;
      script.onload = resolve;
      script.onerror = reject;
      document.head.appendChild(script);
    });
  }

  // Load image asynchronously
  loadImage(src) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous'; // Handle CORS if needed
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = src;
    });
  }

  // Format phone number for WhatsApp (add country code if missing)
  formatPhoneNumberForWhatsApp(phoneNumber) {
    // Remove all non-digit characters
    let cleanPhone = phoneNumber.replace(/[^\d]/g, '');
    
    // If phone number starts with 0, remove it
    if (cleanPhone.startsWith('0')) {
      cleanPhone = cleanPhone.substring(1);
    }
    
    // If phone number is 10 digits, add 91 country code
    if (cleanPhone.length === 10) {
      cleanPhone = '91' + cleanPhone;
    }
    
    return cleanPhone;
  }

  // iOS-compatible WhatsApp opening
  openWhatsAppIOS(whatsappUrl) {
    // Detect iOS
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) || 
                  (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    
    if (isIOS) {
      // For iOS, try multiple methods for better compatibility
      try {
        // Method 1: Create a temporary link and click it
        const link = document.createElement('a');
        link.href = whatsappUrl;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        
        // Add to DOM temporarily
        link.style.display = 'none';
        document.body.appendChild(link);
        
        // Trigger click
        link.click();
        
        // Clean up after a short delay
        setTimeout(() => {
          if (document.body.contains(link)) {
            document.body.removeChild(link);
          }
        }, 100);
        
        // Method 2: Fallback - try window.location as backup
        setTimeout(() => {
          try {
            window.location.href = whatsappUrl;
          } catch (e) {
            console.warn('WhatsApp fallback failed:', e);
          }
        }, 500);
        
      } catch (error) {
        // Method 3: Direct location change as last resort
        console.warn('Link click failed, trying direct location change:', error);
        try {
          window.location.href = whatsappUrl;
        } catch (e) {
          console.error('All WhatsApp opening methods failed:', e);
        }
      }
    } else {
      // For other platforms, use window.open
      window.open(whatsappUrl, '_blank');
    }
  }

  // Draw logo placeholder
  drawLogoPlaceholder(ctx, width, padding, y) {
    const logoX = width - padding - 40;
    const logoY = y - 30;
    ctx.fillStyle = '#10b981';
    ctx.beginPath();
    ctx.arc(logoX, logoY, 20, 0, 2 * Math.PI);
    ctx.fill();
    ctx.fillStyle = '#ffffff';
    ctx.font = `bold 12px Arial, sans-serif`;
    ctx.textAlign = 'center';
    ctx.fillText('LOGO', logoX, logoY + 4);
    ctx.textAlign = 'left'; // Reset alignment
  }

  // Normalize invoice data to ensure consistent structure
  normalizeInvoiceData(invoiceData) {
    return {
      billName: invoiceData.billName || invoiceData.name || 'Invoice',
      customerName: invoiceData.customerName || 'Customer',
      customerPhone: invoiceData.customerPhone || invoiceData.phone || '',
      items: invoiceData.items || [],
      total: invoiceData.total || 0,
      subtotal: invoiceData.subtotal || invoiceData.total || 0,
      discount: invoiceData.discount || 0
    };
  }

  // Unified invoice drawing function - works for both image and PDF
  drawUnifiedInvoice(ctx, invoiceData, orgData, width, logoImage = null) {
    // Normalize the data to ensure consistent structure
    const normalizedData = this.normalizeInvoiceData(invoiceData);
    
    // Use the best rendering logic (from the current drawInvoice function)
    this.drawInvoice(ctx, normalizedData, orgData, width, logoImage);
  }

  drawInvoice(ctx, invoiceData, orgData, width, logoImage = null) {
    // Normalize the data to ensure consistent structure
    const normalizedData = this.normalizeInvoiceData(invoiceData);
    const padding = 40;
    let y = padding;
    const lineHeight = 25;
    const fontSize = 14;
    const headerFontSize = 24;
    const titleFontSize = 18;
    const sectionFontSize = 16;
    const itemFontSize = 12;
    
    // Set canvas background
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, width, 1200);
    
    // ===== SECTION 1: BUSINESS HEADER =====
    // Green top border
    ctx.fillStyle = '#10b981';
    ctx.fillRect(0, 0, width, 4);
    
    // Business name
    ctx.fillStyle = '#10b981';
    ctx.font = `bold ${headerFontSize}px Arial, sans-serif`;
    ctx.fillText(orgData?.name || 'Your Business', padding, y);
    
    // Logo (if available) - use pre-loaded image
    if (logoImage) {
      try {
        const logoSize = 40;
        const logoX = width - padding - logoSize;
        const logoY = y - 35;
        
        // Draw logo with high quality
        ctx.save();
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(logoImage, logoX, logoY, logoSize, logoSize);
        ctx.restore();
      } catch (error) {
        // Fallback to placeholder if logo drawing fails
        this.drawLogoPlaceholder(ctx, width, padding, y);
      }
    } else {
      // Logo placeholder (green circle) if no logo
      this.drawLogoPlaceholder(ctx, width, padding, y);
    }
    
    y += lineHeight + 10;
    
    // Business details
    ctx.font = `${fontSize}px Arial, sans-serif`;
    ctx.fillStyle = '#374151';
    if (orgData?.address) {
      ctx.fillText(orgData.address, padding, y);
      y += lineHeight;
    }
    if (orgData?.phone) {
      ctx.fillText(`Phone: ${orgData.phone}`, padding, y);
      y += lineHeight;
    }
    if (orgData?.email) {
      ctx.fillText(`Email: ${orgData.email}`, padding, y);
      y += lineHeight;
    }
    
    y += 30;
    
    // Invoice title with green background
    ctx.fillStyle = '#10b981';
    ctx.fillRect(padding, y - 10, width - (padding * 2), 35);
    ctx.fillStyle = '#ffffff';
    ctx.font = `bold ${titleFontSize}px Arial, sans-serif`;
    ctx.fillText(`INVOICE: ${normalizedData.billName}`, padding + 15, y + 10);
    
    // Invoice date
    ctx.fillStyle = '#ffffff';
    ctx.font = `${fontSize}px Arial, sans-serif`;
    ctx.fillText(`Date: ${new Date().toLocaleDateString()}`, width - padding - 150, y + 10);
    
    y += 45;
    
    // ===== SECTION 2: CUSTOMER DETAILS =====
    // Customer header
    ctx.fillStyle = '#10b981';
    ctx.font = `bold ${sectionFontSize}px Arial, sans-serif`;
    ctx.fillText('Bill To:', padding, y);
    y += lineHeight + 5;
    
    // Customer details
    ctx.font = `${fontSize}px Arial, sans-serif`;
    ctx.fillStyle = '#1f2937';
    if (normalizedData.customerName) {
      ctx.fillText(normalizedData.customerName, padding, y);
      y += lineHeight;
    }
    if (normalizedData.customerPhone) {
      ctx.fillText(normalizedData.customerPhone, padding, y);
      y += lineHeight;
    }
    y += 20;
    
    // ===== SECTION 3: ITEMS TABLE =====
    // Table header with green background
    ctx.fillStyle = '#10b981';
    ctx.fillRect(padding, y - 5, width - (padding * 2), 30);
    ctx.fillStyle = '#ffffff';
    ctx.font = `bold ${itemFontSize}px Arial, sans-serif`;
    
    // Table headers with centered alignment
    ctx.textAlign = 'left';
    ctx.fillText('Item Description', padding + 10, y + 10);
    ctx.textAlign = 'center';
    ctx.fillText('Qty', padding + 350, y + 10);
    ctx.textAlign = 'right';
    ctx.fillText('Unit Price', padding + 500, y + 10);
    ctx.fillText('Total', padding + 650, y + 10);
    
    y += 35;
    
    // Items list with alternating row colors
    ctx.font = `${itemFontSize}px Arial, sans-serif`;
    
    normalizedData.items.forEach((item, index) => {
      const hasItemDiscount = item.discount && item.discount > 0;
      const itemHeight = hasItemDiscount ? 45 : 30;
      
      // Alternate row colors
      if (index % 2 === 0) {
        ctx.fillStyle = '#f9fafb';
        ctx.fillRect(padding, y - 5, width - (padding * 2), itemHeight);
      } else {
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(padding, y - 5, width - (padding * 2), itemHeight);
      }
      
      ctx.fillStyle = '#1f2937';
      ctx.textAlign = 'left';
      ctx.font = `${itemFontSize}px Arial, sans-serif`;
      ctx.fillText(item.name, padding + 10, y + 10);
      ctx.textAlign = 'center';
      ctx.fillText(item.quantity.toString(), padding + 350, y + 10);
      ctx.textAlign = 'right';
      ctx.fillText(`₹${item.price.toFixed(2)}`, padding + 500, y + 10);
      
      // Show item with discount
      if (hasItemDiscount) {
        const originalTotal = item.originalTotal || (item.price * item.quantity);
        // Original price with strikethrough
        ctx.fillStyle = '#9ca3af';
        ctx.font = `${itemFontSize - 1}px Arial, sans-serif`;
        ctx.fillText(`₹${originalTotal.toFixed(2)}`, padding + 650, y + 10);
        // Draw strikethrough line
        ctx.beginPath();
        ctx.moveTo(padding + 550, y + 8);
        ctx.lineTo(padding + 640, y + 8);
        ctx.strokeStyle = '#9ca3af';
        ctx.lineWidth = 1;
        ctx.stroke();
        
        y += 15;
        
        // Discount amount in green
        ctx.fillStyle = '#10b981';
        ctx.font = `bold ${itemFontSize - 1}px Arial, sans-serif`;
        ctx.textAlign = 'right';
        ctx.fillText(`-₹${item.discount.toFixed(2)}`, padding + 650, y + 10);
        
        y += 15;
        
        // Final total
        ctx.fillStyle = '#1f2937';
        ctx.font = `bold ${itemFontSize}px Arial, sans-serif`;
        ctx.fillText(`₹${item.lineTotal.toFixed(2)}`, padding + 650, y + 10);
      } else {
        ctx.fillText(`₹${(item.price * item.quantity).toFixed(2)}`, padding + 650, y + 10);
      }
      
      y += itemHeight;
    });
    
    y += 15;
    
    // ===== TOTAL SECTION =====
    const hasDiscount = normalizedData.discount > 0;
    const itemDiscountTotal = normalizedData.items.reduce((sum, item) => sum + (item.discount || 0), 0);
    const overallDiscount = normalizedData.discount - itemDiscountTotal;
    const totalHeight = hasDiscount ? 90 : 35;
    
    // Total background with green accent
    ctx.fillStyle = '#f0fdf4';
    ctx.fillRect(padding, y - 5, width - (padding * 2), totalHeight);
    ctx.fillStyle = '#10b981';
    ctx.fillRect(padding, y - 5, 4, totalHeight);
    
    ctx.textAlign = 'left';
    ctx.fillStyle = '#374151';
    ctx.font = `${fontSize}px Arial, sans-serif`;
    
    // Subtotal
    ctx.fillText('Subtotal:', padding + 10, y + 10);
    ctx.textAlign = 'right';
    ctx.fillText(`₹${normalizedData.subtotal.toFixed(2)}`, width - padding - 10, y + 10);
    y += lineHeight;
    
    // Item-level discounts summary (if any)
    if (itemDiscountTotal > 0) {
      ctx.textAlign = 'left';
      ctx.fillStyle = '#10b981';
      ctx.font = `bold ${fontSize}px Arial, sans-serif`;
      ctx.fillText('Item Discounts:', padding + 10, y + 10);
      ctx.textAlign = 'right';
      ctx.fillText(`-₹${itemDiscountTotal.toFixed(2)}`, width - padding - 10, y + 10);
      y += lineHeight;
    }
    
    // Overall discount (if any)
    if (overallDiscount > 0) {
      ctx.textAlign = 'left';
      ctx.fillStyle = '#10b981';
      ctx.font = `bold ${sectionFontSize}px Arial, sans-serif`;
      ctx.fillText('Overall Discount:', padding + 10, y + 10);
      ctx.textAlign = 'right';
      ctx.fillText(`-₹${overallDiscount.toFixed(2)}`, width - padding - 10, y + 10);
      y += lineHeight;
    }
    
    // Show total savings if discount is applied
    if (hasDiscount) {
      ctx.textAlign = 'center';
      ctx.fillStyle = '#059669';
      ctx.font = `bold ${sectionFontSize}px Arial, sans-serif`;
      ctx.fillText(`🎉 You Saved ₹${normalizedData.discount.toFixed(2)}! 🎉`, width / 2, y + 10);
      y += lineHeight + 5;
    }
    
    // Total amount with proper alignment
    ctx.textAlign = 'left';
    ctx.fillStyle = '#10b981';
    ctx.font = `bold ${sectionFontSize}px Arial, sans-serif`;
    ctx.fillText('Total Amount:', padding + 10, y + 10);
    
    ctx.fillStyle = '#10b981';
    ctx.font = `bold 20px Arial, sans-serif`;
    ctx.textAlign = 'right';
    ctx.fillText(`₹${normalizedData.total.toFixed(2)}`, width - padding - 10, y + 10);
    
    y += 50;
    
    // ===== FOOTER SECTION =====
    // Thank you message - centered (use custom footer text if available)
    const footerText = orgData?.footerText || 'Thank you for your business!';
    ctx.fillStyle = '#10b981';
    ctx.font = `bold ${fontSize}px Arial, sans-serif`;
    ctx.textAlign = 'center';
    ctx.fillText(footerText, width / 2, y);
    y += lineHeight + 10;
    
    // Footer details - centered
    ctx.fillStyle = '#6b7280';
    ctx.font = `${fontSize - 2}px Arial, sans-serif`;
    ctx.textAlign = 'center';
    ctx.fillText(`Invoice generated on: ${new Date().toLocaleDateString()} at ${new Date().toLocaleTimeString()}`, width / 2, y);
    y += lineHeight;
    ctx.fillText('This is a computer-generated invoice.', width / 2, y);
    
    // Bottom green border
    ctx.fillStyle = '#10b981';
    ctx.fillRect(0, y + 20, width, 3);
  }

  // Send invoice via WhatsApp with preloaded customer and file
  async sendInvoiceViaWhatsApp(phoneNumber, invoiceData, orgData, options = {}) {
    try {
      const { includeImage = true } = options;
      
      // Create WhatsApp message
      const message = this.createWhatsAppMessage(invoiceData, orgData);
      
      // If not including image, just send the message with link
      if (!includeImage) {
        // Clean and format phone number for WhatsApp
        const cleanPhone = this.formatPhoneNumberForWhatsApp(phoneNumber);
        
        // Create WhatsApp URL with pre-filled message
        const whatsappUrl = `https://wa.me/${cleanPhone}?text=${encodeURIComponent(message)}`;
        
        // iOS-compatible WhatsApp opening
        this.openWhatsAppIOS(whatsappUrl);
        
        return {
          success: true,
          message: 'Invoice opened in WhatsApp!',
          method: 'direct-whatsapp'
        };
      }
      
      // Original behavior with image attachment
      let fileBlob;
      let fileName;
      let fileType;
      
      try {
        fileBlob = await this.generateInvoiceImage(invoiceData, orgData);
        fileName = `invoice-${(invoiceData.customerName || 'customer').toLowerCase().replace(/[^a-z0-9]+/gi,'-')}-${Date.now()}.png`;
        fileType = 'image/png';
      } catch (imageError) {
        // Fallback to PDF if image generation fails
        fileBlob = await this.generateInvoicePDF(invoiceData, orgData);
        fileName = `invoice-${(invoiceData.customerName || 'customer').toLowerCase().replace(/[^a-z0-9]+/gi,'-')}-${Date.now()}.pdf`;
        fileType = 'application/pdf';
      }
      
      // Try Web Share API first (works on mobile)
      if (navigator.share && navigator.canShare && navigator.canShare({ files: [new File([fileBlob], fileName, { type: fileType })] })) {
        try {
          await navigator.share({
            title: `Invoice: ${invoiceData.billName}`,
            text: message,
            files: [new File([fileBlob], fileName, { type: fileType })]
          });
          
          return {
            success: true,
            message: 'Invoice shared via WhatsApp!',
            method: 'web-share'
          };
        } catch (shareError) {
        }
      }
      
      // Use WhatsApp preloader for desktop/fallback
      const { whatsappPreloader } = await import('./WhatsAppPreloader');
      
      // Format phone number for WhatsApp
      const formattedPhone = this.formatPhoneNumberForWhatsApp(phoneNumber);
      
      const result = await whatsappPreloader.initializeWithCustomer(
        formattedPhone,
        message,
        fileBlob,
        fileName
      );
      
      return result;
      
    } catch (error) {
      throw error;
    }
  }

  createWhatsAppMessage(invoiceData, orgData = null) {
    try {
      // Use the imported encoding utility
      return generateWhatsAppMessageWithPublicLink(invoiceData, null, orgData);
    } catch (error) {
      console.error('Error creating WhatsApp message with public link:', error);
      
      // Fallback to simple URL generation
      const baseUrl = window.location.origin;
      const invoiceUrl = `${baseUrl}/?inv=${encodeURIComponent(invoiceData.billName || 'Invoice')}`;
      
      // Limit items to first 3 to avoid message length issues
      const maxItems = 3;
      const items = invoiceData.items || [];
      const displayItems = items.slice(0, maxItems);
      const hasMoreItems = items.length > maxItems;
      
      const hasDiscount = invoiceData.discount && invoiceData.discount > 0;
      
      // Calculate item-level discounts
      const itemsWithDiscounts = displayItems.filter(item => item.discountPercent && item.discountPercent > 0);
      const itemDiscountTotal = itemsWithDiscounts.reduce((sum, item) => {
        const baseTotal = item.price * item.quantity;
        return sum + (baseTotal * item.discountPercent / 100);
      }, 0);
      const overallDiscount = invoiceData.discount - itemDiscountTotal;
      
      // Build items list with discounts
      const itemsList = displayItems.map(item => {
        const baseTotal = item.price * item.quantity;
        const itemDiscount = item.discountPercent ? (baseTotal * item.discountPercent / 100) : 0;
        if (itemDiscount > 0) {
          return `• ${item.name}
   ₹${item.price} × ${item.quantity} = ₹${baseTotal.toFixed(2)}
   🎁 Discount ${item.discountPercent}%: -₹${itemDiscount.toFixed(2)}
   ✅ Total: ₹${(baseTotal - itemDiscount).toFixed(2)}`;
        }
        return `• ${item.name} - ₹${item.price} × ${item.quantity} = ₹${baseTotal.toFixed(2)}`;
      }).join('\n\n');
      
      const discountSection = hasDiscount ? `
━━━━━━━━━━━━━━━━━━━━
🎉 *DISCOUNT SUMMARY* 🎉
━━━━━━━━━━━━━━━━━━━━
Subtotal: ₹${(invoiceData.subtotal || invoiceData.total || 0).toFixed(2)}
${itemDiscountTotal > 0 ? `Item Discounts: -₹${itemDiscountTotal.toFixed(2)}\n` : ''}${overallDiscount > 0 ? `Overall Discount: -₹${overallDiscount.toFixed(2)}\n` : ''}━━━━━━━━━━━━━━━━━━━━
🎁 *YOU SAVED ₹${invoiceData.discount.toFixed(2)}!* 🎁
━━━━━━━━━━━━━━━━━━━━
` : '';

      // Get footer text from orgData or invoiceData, fallback to default
      const footerText = orgData?.footerText || invoiceData.footerText || 'Thank you for your business!';

      return `
📄 *INVOICE: ${invoiceData.billName || 'Invoice'}*

👤 *Customer:* ${invoiceData.customerName}
📞 *Phone:* ${invoiceData.customerPhone}

📋 *Items:*
${itemsList || 'No items'}
${hasMoreItems ? `\n• ... and ${items.length - maxItems} more items` : ''}
${discountSection}
💰 *Total Amount: ₹${invoiceData.total?.toFixed(2) || '0.00'}*

🔗 *View Full Invoice Online:* 
${invoiceUrl}

${footerText} 🙏

Click the link above to view your invoice with full details and payment options.
      `.trim();
    }
  }

  // Alternative method: Create message with image data URL
  async createWhatsAppMessageWithImage(invoiceData, orgData) {
    try {
      // Generate invoice image
      const imageBlob = await this.generateInvoiceImage(invoiceData, orgData);
      
      // Convert to data URL
      const imageDataUrl = await new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.readAsDataURL(imageBlob);
      });
      
      // Create message with image
      const message = `
📄 *INVOICE: ${invoiceData.billName || 'Invoice'}*

👤 *Customer:* ${invoiceData.customerName}
📞 *Phone:* ${invoiceData.customerPhone}

📋 *Items:*
${invoiceData.items.map(item => 
  `• ${item.name}
   ₹${item.price} × ${item.quantity} = ₹${(item.price * item.quantity).toFixed(2)}`
).join('\n\n')}

💰 *Total Amount: ₹${invoiceData.total.toFixed(2)}*

Thank you for your business! 🙏

*Invoice Image:*
${imageDataUrl}
      `.trim();
      
      return {
        message: message,
        imageDataUrl: imageDataUrl,
        imageBlob: imageBlob
      };
    } catch (error) {
      throw error;
    }
  }

  downloadInvoiceImage(imageBlob, customerName) {
    const filename = `invoice-${(customerName || 'customer').toLowerCase().replace(/[^a-z0-9]+/gi,'-')}-${Date.now()}.png`;
    const downloadLink = document.createElement('a');
    downloadLink.href = URL.createObjectURL(imageBlob);
    downloadLink.download = filename;
    downloadLink.style.display = 'none';
    document.body.appendChild(downloadLink);
    downloadLink.click();
    
    // Clean up
    setTimeout(() => {
      document.body.removeChild(downloadLink);
    }, 1000);
  }

  // Alternative method: Generate invoice as HTML and convert to image
  async generateInvoiceHTML(invoiceData, orgData) {
    const html = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <style>
          body {
            font-family: Arial, sans-serif;
            margin: 0;
            padding: 40px;
            background: white;
            width: 800px;
          }
          .header {
            text-align: center;
            margin-bottom: 30px;
            border-bottom: 2px solid #333;
            padding-bottom: 20px;
          }
          .company-name {
            font-size: 24px;
            font-weight: bold;
            color: #333;
            margin-bottom: 10px;
          }
          .company-details {
            font-size: 14px;
            color: #666;
            line-height: 1.4;
          }
          .invoice-title {
            font-size: 20px;
            font-weight: bold;
            color: #333;
            margin: 20px 0;
          }
          .customer-info {
            background: #f5f5f5;
            padding: 15px;
            margin: 20px 0;
            border-radius: 5px;
          }
          .items-table {
            width: 100%;
            border-collapse: collapse;
            margin: 20px 0;
          }
          .items-table th,
          .items-table td {
            border: 1px solid #ddd;
            padding: 12px;
            text-align: left;
          }
          .items-table th {
            background: #f5f5f5;
            font-weight: bold;
          }
          .total {
            font-size: 18px;
            font-weight: bold;
            text-align: right;
            margin: 20px 0;
            padding: 15px;
            background: #f0f8ff;
            border-radius: 5px;
          }
          .footer {
            text-align: center;
            margin-top: 30px;
            padding-top: 20px;
            border-top: 1px solid #ddd;
            color: #666;
            font-size: 14px;
          }
        </style>
      </head>
      <body>
        <div class="header">
          <div class="company-name">${orgData?.name || 'Your Company'}</div>
          <div class="company-details">
            ${orgData?.address ? `<div>${orgData.address}</div>` : ''}
            ${orgData?.phone ? `<div>Phone: ${orgData.phone}</div>` : ''}
            ${orgData?.email ? `<div>Email: ${orgData.email}</div>` : ''}
          </div>
        </div>
        
        <div class="invoice-title">INVOICE: ${invoiceData.billName || 'Invoice'}</div>
        
        <div class="customer-info">
          <strong>Customer:</strong> ${invoiceData.customerName}<br>
          <strong>Phone:</strong> ${invoiceData.customerPhone}
        </div>
        
        <table class="items-table">
          <thead>
            <tr>
              <th>Item</th>
              <th>Price</th>
              <th>Quantity</th>
              <th>Total</th>
            </tr>
          </thead>
          <tbody>
            ${invoiceData.items.map(item => {
              const baseTotal = item.price * item.quantity;
              const itemDiscount = item.discountPercent ? (baseTotal * item.discountPercent / 100) : 0;
              const hasItemDiscount = itemDiscount > 0;
              
              if (hasItemDiscount) {
                return `
              <tr>
                <td>${item.name}</td>
                <td>₹${item.price}</td>
                <td>${item.quantity}</td>
                <td>
                  <div style="text-decoration: line-through; color: #9ca3af; font-size: 12px;">₹${baseTotal.toFixed(2)}</div>
                  <div style="color: #10b981; font-weight: bold; font-size: 13px;">-₹${itemDiscount.toFixed(2)} (${item.discountPercent}%)</div>
                  <div style="font-weight: bold; color: #1f2937;">₹${(baseTotal - itemDiscount).toFixed(2)}</div>
                </td>
              </tr>
            `;
              }
              return `
              <tr>
                <td>${item.name}</td>
                <td>₹${item.price}</td>
                <td>${item.quantity}</td>
                <td>₹${baseTotal.toFixed(2)}</td>
              </tr>
            `;
            }).join('')}
          </tbody>
        </table>
        
        ${invoiceData.discount && invoiceData.discount > 0 ? (() => {
          const itemDiscountTotal = invoiceData.items.reduce((sum, item) => {
            const baseTotal = item.price * item.quantity;
            const itemDiscount = item.discountPercent ? (baseTotal * item.discountPercent / 100) : 0;
            return sum + itemDiscount;
          }, 0);
          const overallDiscount = invoiceData.discount - itemDiscountTotal;
          
          return `
        <div style="text-align: right; margin: 20px 0; padding: 15px; background: #f0fdf4; border-radius: 5px; border-left: 4px solid #10b981;">
          <div style="font-size: 14px; color: #374151; margin-bottom: 5px;">
            Subtotal: ₹${(invoiceData.subtotal || invoiceData.total || 0).toFixed(2)}
          </div>
          ${itemDiscountTotal > 0 ? `
          <div style="font-size: 15px; font-weight: bold; color: #10b981; margin-bottom: 5px;">
            Item Discounts: -₹${itemDiscountTotal.toFixed(2)}
          </div>
          ` : ''}
          ${overallDiscount > 0 ? `
          <div style="font-size: 15px; font-weight: bold; color: #10b981; margin-bottom: 5px;">
            Overall Discount: -₹${overallDiscount.toFixed(2)}
          </div>
          ` : ''}
          <div style="font-size: 16px; font-weight: bold; color: #059669; margin: 10px 0; padding: 10px; background: #d1fae5; border-radius: 3px; text-align: center;">
            🎉 You Saved ₹${invoiceData.discount.toFixed(2)}! 🎉
          </div>
          <div style="font-size: 18px; font-weight: bold; color: #10b981; border-top: 1px solid #10b981; padding-top: 10px; margin-top: 10px;">
            Total Amount: ₹${invoiceData.total.toFixed(2)}
          </div>
        </div>
        `;
        })() : `
        <div class="total">
          Total Amount: ₹${invoiceData.total.toFixed(2)}
        </div>
        `}
        
        <div class="footer">
          <div>${orgData?.footerText || 'Thank you for your business!'}</div>
          <div>Generated on: ${new Date().toLocaleDateString()}</div>
        </div>
      </body>
      </html>
    `;
    
    return html;
  }
}

// Export singleton instance
export const invoiceImageService = new InvoiceImageService();
